import { z } from 'zod';
import { overrideZoom, overrideZoomParameter, numberParameter } from './shared.js';

const focusCount = z.number().int().min(1).max(18);

const quadrantOptionValues = {
  zoom: overrideZoom,
  first_focus: z.number().nullable().optional(),
  focus_increment: z.number().nullable().optional(),
  focus_count: focusCount.nullable().optional(),
  rays_across_diameter: z.number().int().nonnegative().nullable().optional(),
};
const quadrantOption = z.union([
  z.strictObject({ ...quadrantOptionValues, first_focus: z.number() }),
  z.strictObject({ ...quadrantOptionValues, focus_increment: z.number() }),
  z.strictObject({ ...quadrantOptionValues, focus_count: focusCount }),
  z.strictObject({ ...quadrantOptionValues, rays_across_diameter: z.number().int().nonnegative() }),
  z.strictObject({ ...quadrantOptionValues, smoothing: z.literal('diameter'), smoothing_diameter: z.number() }),
  z.strictObject({ ...quadrantOptionValues, smoothing: z.literal('No') }),
]);
const quadrantSettings = z.strictObject({
  scan_steps: z.array(z.strictObject({ zoom: overrideZoom, step: z.number().positive() })).min(1).max(20)
    .describe('STE: one to twenty ordered scan steps in native lens length units. Each step is explicit; no scan positions are generated.'),
  option_settings: z.array(quadrantOption).default([])
    .describe('Ordered focus, NRD and GAU overrides. Focus settings are applied before QUA. Omitted cells send no command. Empty retains native sampling, context-dependent Gaussian smoothing and loaded FFO/IFO/NFO. NRD is a nonnegative integer count across the pupil diameter; zero retains native sampling; wavelength weights control the other native grids. GAU diameter is the Gaussian spot diameter at half peak intensity. Native No is passed unchanged; it must not be interpreted as disabling all smoothing.'),
  response_requests: z.array(z.union([
    z.strictObject({ zoom: overrideZoom, mode: z.literal('ratio'), ratio: z.number() }),
    z.strictObject({ zoom: overrideZoom, mode: z.literal('No') }),
  ])).max(20).default([])
    .describe('QRE: up to twenty ordered selective-response requests. Empty requests none. Native No adds no response row and does not clear earlier requests. Only native printed ratios, positions and RAD ENERGY are returned; reciprocal positions are not synthesized.'),
});

export default {
    label: 'Quadrant Detector Response', group: 'performance',
    description: 'Quadrant-detector response ratios against detector scan position.',
    settingsSchema: quadrantSettings,
    parameters: [
      { key: 'scan_steps', label: 'Scan steps', kind: 'rows', prominent: true, columns: [
        overrideZoomParameter, numberParameter('step', 'Step', { unit: 'system length' }),
      ] },
      { key: 'option_settings', label: 'Options', kind: 'rows', columns: [
        overrideZoomParameter,
        { ...numberParameter('first_focus', 'First focus', { unit: 'system length', blank: 'Unchanged' }), omit: { first: 'Loaded lens', later: 'Unchanged' } },
        { ...numberParameter('focus_increment', 'Focus increment', { unit: 'system length', blank: 'Unchanged' }), omit: { first: 'Loaded lens', later: 'Unchanged' } },
        // PromptingGuide p30: NFO "Number of focus steps - up to 18".
        { ...numberParameter('focus_count', 'Focus count', { integer: true, blank: 'Unchanged', range: '1–18' }), omit: { first: 'Loaded lens', later: 'Unchanged' } },
        { ...numberParameter('rays_across_diameter', 'Rays across diameter', { integer: true, blank: 'Unchanged' }), omit: { first: 'CODE V default', later: 'Unchanged' } },
        { key: 'smoothing', label: 'Gaussian smoothing', kind: 'select', options: [
          { value: 'diameter', label: 'Explicit diameter' }, { value: 'No', label: 'No' },
        ], omit: { first: 'CODE V default', later: 'Unchanged' } },
        numberParameter('smoothing_diameter', 'Smoothing diameter', { unit: 'system length', when: { smoothing: ['diameter'] } }),
      ] },
      { key: 'response_requests', label: 'Response requests', kind: 'rows', columns: [
        overrideZoomParameter,
        { key: 'mode', label: 'Request', kind: 'select', options: [
          { value: 'ratio', label: 'Response ratio' }, { value: 'No', label: 'No' },
        ] },
        numberParameter('ratio', 'Ratio', { when: { mode: ['ratio'] } }),
      ] },
    ],
    coverage: 'Geometrical response only; diffraction is not included. Covers all fields, using the lens wavelength weights. CODE V can omit an axis when the lens is symmetric. Any smoothing is applied by CODE V. Afocal systems are not supported.',
  } as const;
